import { observer } from 'mobx-react-lite'
import {
  DatePicker,
  Dropdown,
  DropdownContent,
  Label,
  PhoneWithLabelInput,
  Typography,
} from 'shared/ui'

import { TextField, makeTextFieldIcon } from 'shared/ui/TextField'
import { TimePicker } from 'shared/ui/DatePicker'
import { uiStore } from 'shared/store/uiStore'
import { DayjsFormats } from 'shared/lib'
import type { OpportunityModalStore } from 'pages/settings/pages/billing/pages/overview/ui/OpportunityModal/store/opportunityModalStore'
import styles from './styles.module.scss'

export const GetCallModalContent = observer(({ store }: { store: OpportunityModalStore }) => {
  const { phoneInputStore, selectedDate, setSelectedDate, isLoading } = store

  const offsetMinutes = uiStore.offsetMinute

  const handleDateChange = (newDate: Date | null) => {
    if (!newDate) {
      setSelectedDate(null)
      return
    }

    if (selectedDate) {
      const currentTime = uiStore.dayjs(selectedDate)
      const newDateWithTime = uiStore
        .dayjs(newDate)
        .hour(currentTime.hour())
        .minute(currentTime.minute())
        .second(currentTime.second())
        .millisecond(currentTime.millisecond())
      setSelectedDate(newDateWithTime.toDate())
    } else {
      setSelectedDate(newDate)
    }
  }

  return (
    <div className={styles.content}>
      <Typography ariaLabel='get_call_modal_title' variant='heading-lg' marginBottom={16}>
        When should we call you?
      </Typography>
      <div className={styles.inputWrap}>
        <Label
          tertiary={true}
          label={{
            text: 'Phone number',
          }}
        />
        <PhoneWithLabelInput
          item={phoneInputStore.country}
          items={phoneInputStore.countries}
          number={phoneInputStore.number}
          hideLabel
          disabled={isLoading}
          setNumber={phoneInputStore.setNumber}
          setCountry={phoneInputStore.setCountry}
          triggerNumberValidation={phoneInputStore.triggerNumberValidation}
          isValidNumber={phoneInputStore.isValidNumber}
          numberError={phoneInputStore.numberError}
          numberRequestError={phoneInputStore.numberRequestError}
        />
      </div>
      <div className={styles.inputs}>
        <div className={styles.inputWrap}>
          <Label
            tertiary={true}
            label={{
              text: 'Date',
            }}
          />
          <Dropdown
            width={304}
            placement={'bottom-start'}
            customComponent={(onCloseMenu) => (
              <DropdownContent noPadding>
                <DatePicker
                  type={'singleInline'}
                  date={selectedDate}
                  handleDate={(value) => {
                    handleDateChange(value)
                    onCloseMenu()
                  }}
                  minDate={uiStore.dayjs().toDate()}
                  excludeDateIntervals={[
                    {
                      start: new Date(0),
                      end: uiStore.dayjs().add(-1, 'day').toDate(),
                    },
                  ]}
                />
              </DropdownContent>
            )}
            triggerComponent={() => (
              <TextField
                variant={'stroke'}
                size={'medium'}
                InputProps={{
                  placeholder: 'Select date',
                }}
                leftActions={[
                  makeTextFieldIcon({ icon: 'calendar', className: styles.datePickerIcon }),
                ]}
                className={styles.inputDatePicker}
                value={selectedDate ? uiStore.dayjs(selectedDate).format(DayjsFormats.full6) : ''}
              />
            )}
          />
        </div>
        <div className={styles.inputWrap}>
          <Label
            tertiary={true}
            label={{
              text: 'Time',
            }}
          />
          <TimePicker
            selected={selectedDate}
            onChange={(value) => {
              setSelectedDate(value)
            }}
            filteredPassedTime={uiStore.dayjs().add(offsetMinutes, 'minutes').toDate()}
            offsetMinutes={offsetMinutes}
            minuteStep={15}
            textFieldPlaceholderProps={{
              placeholder: 'Select time',
              className: styles.inputTimePicker,
            }}
          />
        </div>
      </div>
    </div>
  )
})
